import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, PlusIcon, AcademicCapIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { batchAPI, enrollmentAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/dateUtils';
import { useDebounce } from '../../hooks/useDebounce';
import ConfirmationModal from '../ConfirmationModal';

const Batches = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const searchInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Debounce the search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Maintain focus on search input after search results update
  useEffect(() => {
    if (searchInputRef.current && document.activeElement === searchInputRef.current) {
      const cursorPosition = searchInputRef.current.selectionStart;
      searchInputRef.current.focus();
      searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [debouncedSearchTerm]);

  const { data: batches, isLoading, error } = useQuery({
    queryKey: ['batches', currentPage, debouncedSearchTerm],
    queryFn: () => {
      if (debouncedSearchTerm) {
        return batchAPI.search(debouncedSearchTerm).then(res => res.data);
      }
      return batchAPI.getAll(currentPage * itemsPerPage, itemsPerPage, true).then(res => res.data);
    },
  });

  // Fetch enrollment counts for all batches
  const { data: enrollmentCounts, isLoading: enrollmentLoading } = useQuery({
    queryKey: ['batch-enrollment-counts', batches],
    queryFn: async () => {
      if (!batches || batches.length === 0) return {};
      
      const counts = {};
      for (const batch of batches) {
        try {
          const enrollmentsResponse = await enrollmentAPI.getByBatch(batch._id || batch.id);
          counts[batch._id || batch.id] = enrollmentsResponse.data ? enrollmentsResponse.data.length : 0;
        } catch (error) {
          console.error(`Error fetching enrollments for batch ${batch.batch_name}:`, error);
          counts[batch._id || batch.id] = 0;
        }
      }
      return counts;
    },
    enabled: !!batches && batches.length > 0,
  });

  const { data: totalCount, isLoading: countLoading } = useQuery({
    queryKey: ['batches-count'],
    queryFn: () => batchAPI.getCount().then(res => res.data.count),
    enabled: !debouncedSearchTerm, // Only fetch total count when not searching
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => batchAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
    },
  });

  const handleDelete = (id, batchName) => {
    setDeleteTarget({ id, batchName });
    setShowDeleteModal(true);
  };

  const filteredBatches = batches || [];

  if (isLoading || countLoading || enrollmentLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading batches: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Batches</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Manage all batches in Neurons School of Innovation and AI</p>
        </div>
        <Link
          to="/batches/new"
          className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 focus:ring-offset-gray-800"
        >
          <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Add Batch</span>
          <span className="sm:hidden">Add</span>
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm sm:text-base"
            placeholder="Search batches by name, subject, or instructor..."
          />
        </div>
      </div>

      {/* Batches List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-white">
            {debouncedSearchTerm 
              ? `${filteredBatches.length} Batch${filteredBatches.length !== 1 ? 'es' : ''}`
              : `${totalCount || 0} Batch${(totalCount || 0) !== 1 ? 'es' : ''}`
            }
          </h2>
        </div>
        
        {filteredBatches.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <AcademicCapIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-300">No batches found</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-400">
              {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating a new batch.'}
            </p>
            {!searchTerm && (
              <div className="mt-4 sm:mt-6">
                <Link
                  to="/batches/new"
                  className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  Add Batch
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Batch
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Subject
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Time
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Instructor
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Fee
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Students
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filteredBatches.map((batch) => (
                  <tr key={batch._id || batch.id} className="hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-green-600 flex items-center justify-center">
                            <AcademicCapIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm font-medium text-white">
                            {batch.batch_name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-400">
                            Max: {batch.max_students} students
                          </div>
                          <div className="text-xs sm:text-sm text-gray-400">
                            Enrolled: {enrollmentCounts?.[batch._id || batch.id] || 0} students
                          </div>
                          {/* Mobile-only subject info */}
                          <div className="sm:hidden text-xs text-gray-400 mt-1">
                            {batch.subject}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <div>{batch.subject}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                      <div>
                        {batch.start_time && batch.end_time 
                          ? `${batch.start_time} - ${batch.end_time}`
                          : 'Not set'
                        }
                      </div>
                      {batch.days && batch.days.length > 0 && (
                        <div className="text-xs text-gray-400">
                          {Array.isArray(batch.days) ? batch.days.join(', ') : batch.days}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                      <div>{batch.instructor || batch.instructor_name || 'Not assigned'}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                      <div>{batch.fee_amount ? formatIndianCurrency(batch.fee_amount) : 'Not set'}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <div>{enrollmentCounts[batch._id || batch.id] || 0}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        batch.is_active
                          ? 'bg-green-900/30 text-green-300 border border-green-700'
                          : 'bg-red-900/30 text-red-300 border border-red-700'
                      }`}>
                        {batch.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-1 sm:space-x-2">
                        <Link
                          to={`/batches/${batch._id || batch.id}`}
                          className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 rounded-md hover:bg-gray-700 transition-colors"
                        >
                          <AcademicCapIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Link>
                        <Link
                          to={`/batches/${batch._id || batch.id}/edit`}
                          className="p-1 sm:p-2 text-green-400 hover:text-green-300 rounded-md hover:bg-gray-700 transition-colors"
                        >
                          <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(batch._id || batch.id, batch.batch_name)}
                          className="p-1 sm:p-2 text-red-400 hover:text-red-300 rounded-md hover:bg-gray-700 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!searchTerm && filteredBatches.length > 0 && (
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-400 text-center sm:text-left">
              Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalCount || 0)} of {totalCount || 0} results
            </div>
            <div className="flex justify-center sm:justify-end space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={(currentPage + 1) * itemsPerPage >= (totalCount || 0)}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setShowDeleteModal(false);
          }
        }}
        title="Confirm Deletion"
        message={`Are you sure you want to delete ${deleteTarget?.batchName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Batches; 